"use client";

import type React from "react";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { communicationService } from "@/app/(authenticated)/communication/_modules/services/communicationService";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";

type SearchResult =
  | { type: "lead"; id: string; label: string; sub: string; assigneeName?: string | null; assigneeId?: string | null; leadStat?: string | null }
  | { type: "appointment"; id: string; label: string; sub: string }
  | { type: "resource"; id: string; label: string; sub: string }
  | { type: "reminder"; id: string; label: string; sub: string }
  | { type: "call"; id: string; label: string; sub: string }
  | { type: "voicemail"; id: string; label: string; sub: string };

interface SearchContextType {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  searchResults: SearchResult[];
  recentSearches: string[];
  addRecentSearch: (term: string) => void;
  removeRecentSearch: (term: string) => void;
  clearRecentSearches: () => void;
}

const RECENTS_KEY = "ic:recent-searches";
const RECENTS_MAX = 6;

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider = function ({
  children,
}: {
  children: React.ReactNode;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [pendingTerm, setPendingTerm] = useState("");

  // Recent searches — the terms the user actually acted on (clicked a result),
  // persisted so focusing the box shows them for a one-tap re-run (instant from
  // the result cache).
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RECENTS_KEY);
      if (raw) setRecentSearches(JSON.parse(raw).slice(0, RECENTS_MAX));
    } catch {
      /* ignore */
    }
  }, []);
  const addRecentSearch = (term: string) => {
    const t = term.trim();
    if (t.length < 2) return;
    setRecentSearches((prev) => {
      const next = [t, ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(
        0,
        RECENTS_MAX,
      );
      try {
        window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const persistRecents = (next: string[]) => {
    try {
      window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };
  const removeRecentSearch = (term: string) => {
    setRecentSearches((prev) => {
      const next = prev.filter((x) => x !== term);
      persistRecents(next);
      return next;
    });
  };
  const clearRecentSearches = () => {
    setRecentSearches([]);
    persistRecents([]);
  };

  useEffect(() => {
    // Tighter debounce (150ms) — the leads query is trigram-indexed (~1ms), so
    // the wait was the dominant delay. 150ms still coalesces fast typing.
    const t = setTimeout(() => setPendingTerm(searchTerm.trim()), 150);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Ref to hold the AbortController for the current in-flight search requests.
  // When a new search fires, the previous controller is aborted, which cancels
  // all outstanding HTTP requests from the previous batch at the network level.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Short-lived per-term result cache. Backspacing / retyping a prefix you just
  // searched is instant (no network) instead of a fresh 6-call round-trip.
  const cacheRef = useRef<Map<string, { results: SearchResult[]; at: number }>>(
    new Map(),
  );
  const CACHE_TTL_MS = 60_000;
  const CACHE_MAX = 50;

  useEffect(() => {
    // Abort any in-flight requests from the previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    let cancelled = false;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    // Need >=2 chars: the leads typeahead ignores 1-char (trigram noise) and the
    // other 5 categories would just burn round-trips for nothing.
    if (pendingTerm.length < 2) {
      setResults([]);
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    // Serve from cache instantly if fresh — no network at all.
    const cached = cacheRef.current.get(pendingTerm);
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setResults(cached.results);
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    // INCREMENTAL: render each category the instant its own request resolves,
    // in priority order (leads first). Previously a single Promise.all blocked
    // the whole dropdown on the SLOWEST of all six calls — so even a fast leads
    // response sat hidden until calls/voicemails finished. Now leads pop in
    // immediately and the rest fill in as they arrive.
    const term = encodeURIComponent(pendingTerm);
    const byCat: Record<string, SearchResult[]> = {};
    const PRIORITY = ["lead", "appointment", "resource", "reminder", "call", "voicemail"];
    const merge = (cat: string, items: SearchResult[]) => {
      if (cancelled) return;
      byCat[cat] = items;
      const merged = PRIORITY.flatMap((c) => byCat[c] || []).slice(0, 10);
      setResults(merged);
      // Cache the latest merged snapshot for this term (evict oldest at cap).
      cacheRef.current.set(pendingTerm, { results: merged, at: Date.now() });
      if (cacheRef.current.size > CACHE_MAX) {
        const oldest = cacheRef.current.keys().next().value;
        if (oldest !== undefined) cacheRef.current.delete(oldest);
      }
    };
    const arr = (resp: any): any[] =>
      Array.isArray(resp?.data?.items) ? resp.data.items
        : Array.isArray(resp?.items) ? resp.items
          : Array.isArray(resp?.data) ? resp.data
            : Array.isArray(resp) ? resp
              : [];
    const run = (url: string, cat: string, map: (resp: any) => SearchResult[]) =>
      handleApiCall<any>(async () =>
        api.get<any>({ url: getApiUrl(url), auth: true, options: { signal } })
      )
        .then((resp) => merge(cat, map(resp)))
        .catch(() => {
          /* ignore aborts/failures — other categories still render */
        });

    void run(`${endpoint.leads.leadTypeahead}?q=${term}&limit=6`, "lead", (resp) =>
      arr(resp).slice(0, 5).map((l: any) => ({
        type: "lead" as const, id: l.id,
        label: l.name || l.fullName || l.email || "Lead",
        // Contact line only — the assignee now renders as a colored badge, not
        // inline text (see SearchDropdown).
        sub: [
          l.phone ? formatPhoneNumber(l.phone) : l.email || "",
          l.state || "",
        ].filter(Boolean).join(" · "),
        assigneeName: l.assignedToName || null,
        assigneeId: l.assignedToUserId || null,
        leadStat: l.leadStat || null,
      })));
    void run(`${endpoint.appointments.getAppointments}?query=${term}&limit=5`, "appointment", (resp) =>
      arr(resp).slice(0, 5).map((a: any) => ({
        type: "appointment" as const, id: a.id,
        label: a.title || a.subject || "Appointment",
        sub: a.clientName || a.with || a.location || "",
      })));
    void run(`${endpoint.resources.getResources}?search=${term}&limit=5`, "resource", (resp) =>
      arr(resp).slice(0, 5).map((r: any) => ({
        type: "resource" as const, id: r.id,
        label: r.title || r.name || r.filename || "Resource",
        sub: r.mimeType || r.category || "",
      })));
    void run(`${endpoint.reminders.getReminders}?query=${term}&limit=5`, "reminder", (resp) =>
      arr(resp).slice(0, 5).map((r: any) => ({
        type: "reminder" as const, id: r.id,
        label: r.title || r.text || "Reminder",
        sub: r.date || r.status || "",
      })));
    // Calls/voicemails are searched by phone_number OR notes. A NAME term can
    // only match `notes` — an unindexed seq-scan over the user's whole call log
    // (~300ms for a power dialer with 100k+ calls) that almost never returns a
    // useful hit (names are covered by the leads results). So only search
    // calls/voicemails when the term looks like a phone/number (has a digit) —
    // the path that actually uses the phone trigram index and is ~1ms.
    if (/\d/.test(pendingTerm)) {
      void run(`${endpoint.communication.getCalls}?search=${term}&limit=5`, "call", (resp) =>
        arr(resp).slice(0, 5).map((c: any) => ({
          type: "call" as const, id: c.id,
          label: c.contactName || c.phoneNumber || "Call",
          sub: `${c.callStatus || c.status || ""}${c.duration ? ` • ${c.duration}s` : ""}`.trim(),
        })));
      void run(`${endpoint.communication.getCalls}?outcome=voicemail&search=${term}&limit=5`, "voicemail", (resp) =>
        arr(resp).slice(0, 5).map((v: any) => ({
          type: "voicemail" as const, id: v.id,
          label: v.contactName || v.phoneNumber || "Voicemail",
          sub: `${v.callStatus || v.status || ""}${v.duration ? ` • ${v.duration}s` : ""}`.trim(),
        })));
    }

    return () => {
      cancelled = true;
      controller.abort();
    };

  }, [pendingTerm]);

  const searchResults = useMemo(() => results, [results]);

  return (
    <SearchContext.Provider
      value={useMemo(
        function () {
          return { searchTerm, setSearchTerm, searchResults, recentSearches, addRecentSearch, removeRecentSearch, clearRecentSearches };
        },
        [searchResults, searchTerm, recentSearches]
      )}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) throw new Error("useSearch must be used within SearchProvider");
  return context;
};
